import React, { useState } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { useLeagues, LeagueApi, Team } from '../hooks/useLeagues';
import { MLB_TEAM_ABBREVIATIONS } from '../constants';
import { useUserStore } from '../store/userStore';
import { useNavigate } from 'react-router-dom';
import ProcessPayment from './ProcessPayment';
import { useUser } from '../hooks/useUser';
import { useModernToast } from './ModernToast';
import JoinTeamModal from './JoinTeamModal';
import { ROLES } from '../constants/roles';
interface League extends LeagueApi {
  availability: string;
  jobsAvailable?: string;
  playerPool: string;
  name: string;
  code: string;
  tier: string;
  price_league: number;
  teams: Team[];
}

const baseColumns = [
  { label: 'Team Name', accessor: 'name' },
  { label: 'Tier', accessor: 'tier' },
  { label: 'Ownership Fee', accessor: 'price_league' },
  { label: 'League Info', accessor: 'leagueInfo' },
  { label: 'Player Pool', accessor: 'playerPool' },
  // { label: 'Draft Type', accessor: 'draft_type' },
  // { label: 'Availability', accessor: 'availability' },
];

// Helper to get value for a column accessor
function getLeagueValue(league: League, accessor: string): string | number | boolean | undefined {
  switch (accessor) {
    case 'name':
      return league.name;
    case 'tier':
      return league.tier;
    case 'price_league':
      return league.price_league;
    case 'playerPool':
      return league.playerPool;
    case 'draft_type':
      return league.draft_type;
    case 'availability':
      return league.availability;
    case 'jobsAvailable':
      return league.jobsAvailable;
    default:
      return undefined;
  }
}

function SortableTable({ 
  data, 
  showJobsColumn = false, 
  onOwnTeam,
  onJoinTeam 
}: { 
  data: League[], 
  showJobsColumn?: boolean, 
  onOwnTeam: (league: League, team: Team) => void,
  onJoinTeam?: (league: League, team: Team) => void
}) {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const teamNameColWidth = isMobile ? '130px' : undefined;
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (accessor: string) => {
    if (sortBy === accessor) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(accessor);
      setSortDir('asc');
    }
  };

  // Flatten all teams from all leagues for global sorting
  const flattenedTeams = React.useMemo(() => {
    const allTeams: Array<{ team: Team; league: League }> = [];
    data.forEach(league => {
      league.teams.forEach(team => {
        allTeams.push({ team, league });
      });
    });
    // Sort globally
    return allTeams.sort((a, b) => {
      let aValue: string | number = '', bValue: string | number = '';
      if (sortBy === 'name') {
        aValue = a.team.name?.toLowerCase() || '';
        bValue = b.team.name?.toLowerCase() || '';
      } else if (sortBy === 'tier') {
        aValue = (a.team.tier || a.league.tier || '').toLowerCase();
        bValue = (b.team.tier || b.league.tier || '').toLowerCase();
      } else if (sortBy === 'price_league') {
        aValue = a.team.price ?? 0;
        bValue = b.team.price ?? 0;
      } else {
        aValue = (getLeagueValue(a.league, sortBy) as string | number) ?? '';
        bValue = (getLeagueValue(b.league, sortBy) as string | number) ?? '';
      }
      if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortBy, sortDir]);

  const columns = showJobsColumn
    ? [...baseColumns.slice(0, 1), { label: 'Jobs Available', accessor: 'jobsAvailable' }, ...baseColumns.slice(1, -1)]
    : baseColumns;

  const displayColumns = columns.map(col =>
    showJobsColumn && col.accessor === 'price_league'
      ? { ...col, label: col.label + ' *' }
      : col
  );

  return (
    <>
      <Text mb={4} color="whiteAlpha.900" fontSize="lg">
        Ownership fee entitles you to own the team for five years. Buy your team now and manage it through the years!
      </Text>
      <Table
        className="tight-table-padding"
        variant="simple"
        bg="whiteAlpha.100"
        borderRadius="md"
        mt={2}
        px={{ base: 2, md: 6 }}
        py={{ base: 1, md: 3 }}
        minW="640px"
        whiteSpace="nowrap"
      >
        <Thead>
          <Tr>
            {displayColumns.map(col => (
              <Th
                key={col.accessor}
                cursor="pointer"
                onClick={() => handleSort(col.accessor)}
                userSelect="none"
                w={col.accessor === 'name' ? teamNameColWidth : undefined}
                minW={col.accessor === 'name' ? teamNameColWidth : undefined}
                maxW={col.accessor === 'name' ? teamNameColWidth : undefined}
              >
                <Flex align="center">
                  {col.label}
                  {sortBy === col.accessor && (
                    sortDir === 'asc' ? <TriangleUpIcon ml={1} /> : <TriangleDownIcon ml={1} />
                  )}
                </Flex>
              </Th>
            ))}
            <Th>{/* CTA column, no header */}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {flattenedTeams.map(({ league, team }, idx) => (
            <Tr key={team.id || `${league.id}-${idx}`}> 
            {displayColumns.map(col => (
                <Td
                  key={col.accessor}
                  w={col.accessor === 'name' ? teamNameColWidth : undefined}
                  minW={col.accessor === 'name' ? teamNameColWidth : undefined}
                  maxW={col.accessor === 'name' ? teamNameColWidth : undefined}
                >
                  {col.accessor === 'name' ? (
                    <VStack align="flex-start" spacing={isMobile ? 2 : 0} alignItems="flex-start">
                      <Text>{team.name}</Text>
                      {isMobile && (
                        team.owned_by == null ? (
                          <Button
                            variant="dominus"
                            onClick={() => onOwnTeam(league, team)}
                            ml="-6px"
                          >
                            Purchase
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            colorScheme="gray"
                            onClick={() => onJoinTeam?.(league, team)}
                            ml="-6px"
                          >
                            Join Team
                          </Button>
                        )
                      )}
                    </VStack>
                  ) : col.accessor === 'leagueInfo' ? (
                    <Box>
                      <Text>{`${league.name || ''}${league.league_code ? ` (${league.league_code})` : ''}`}</Text>
                      <Text as="i" fontSize="sm" color="whiteAlpha.700">
                        {typeof league.team_count === 'number' ? league.team_count : 30} teams
                      </Text>
                    </Box>
                  ) : col.accessor === 'price_league' ? (
                    `$${(team.price != null ? team.price : 0).toLocaleString()}`
                  ) : col.accessor === 'playerPool' ? (
                    league.playerPool || 'MLB'
                  ) : col.accessor === 'tier' ? (
                    team.tier || league.tier || ''
                  ) : col.accessor === 'jobsAvailable' ? (
                    team.owned_by == null ? "Team Owner" : "All Positions"
                  ) : (
                    typeof getLeagueValue(league, col.accessor) === 'string' ||
                    typeof getLeagueValue(league, col.accessor) === 'number'
                      ? getLeagueValue(league, col.accessor)
                      : ''
                  )}
                </Td>
              ))}
              <Td>
                {team.owned_by == null ? (
                  <Button
                    variant="dominus"
                    fontWeight="normal"
                    onClick={() => onOwnTeam(league, team)}
                  >
                    Purchase
                  </Button>
                ) : (
                  <Button
                    w="full"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => onJoinTeam?.(league, team)}
                  >
                    Join Team
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </>
  );
}

const JoinLeagues = () => {
  const { leagues, isLoading, error } = useLeagues();
  const { leagueAffiliations } = useUser();
  const [teamSearch, setTeamSearch] = useState('');
  const [leagueSearch, setLeagueSearch] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [selectedEntryFee, setSelectedEntryFee] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isJoinTeamModalOpen, setIsJoinTeamModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<{ team: Team; league: League } | null>(null);
  const { user, isAuthenticated } = useUserStore();
  const navigate = useNavigate();
  const toast = useModernToast();
  const searchFieldFlexBasis = useBreakpointValue({
    base: 'calc(50% - 8px)',
    md: '250px',
  });
  const searchFieldMinW = useBreakpointValue({
    base: 'calc(50% - 8px)',
    md: '150px',
  });
  const selectFlexBasis = useBreakpointValue({
    base: 'calc(50% - 8px)',
    md: '180px',
  });
  const selectMinW = useBreakpointValue({
    base: 'calc(50% - 8px)',
    md: '150px',
  });
  const outerPadding = useBreakpointValue({ base: '2px', md: '6px' });
  const tableWrapperPaddingX = useBreakpointValue({ base: '0px', md: '2px' });

  // Add console.log for user data
  // React.useEffect(() => {
  //   console.log('User Data:', {
  //     user,
  //     isAuthenticated,
  //     timestamp: new Date().toISOString()
  //   });
  // }, [user, isAuthenticated]);

  // Filter leagues and teams by search
  const filteredLeagues = React.useMemo(() => {
    const teamSearchLower = teamSearch.trim().toLowerCase();
    const leagueSearchLower = leagueSearch.trim().toLowerCase();

    return leagues
      .map((l: League) => {
        let filteredTeams = l.teams;
        if (teamSearchLower) {
          filteredTeams = l.teams.filter((team: Team) => {
            const abbrEntry = MLB_TEAM_ABBREVIATIONS.find(
              t => t.name.toLowerCase() === team.name.toLowerCase() || t.abbr.toLowerCase() === team.abbreviation.toLowerCase()
            );
            const abbr = abbrEntry ? abbrEntry.abbr.toLowerCase() : '';
            const nameFromAbbr = abbrEntry ? abbrEntry.name.toLowerCase() : '';
            return (
              (team.name && team.name.toLowerCase().includes(teamSearchLower)) ||
              (abbr && abbr.includes(teamSearchLower)) ||
              (nameFromAbbr && nameFromAbbr.includes(teamSearchLower)) ||
              (team.abbreviation && team.abbreviation.toLowerCase().includes(teamSearchLower))
            );
          });
        }
        // Add tier filtering
        if (selectedTier) {
          filteredTeams = filteredTeams.filter((team: Team) =>
            (team.tier || l.tier || '').toLowerCase() === selectedTier.toLowerCase()
          );
        }
        // Add entry fee filtering
        if (selectedEntryFee) {
          const [min, max] = selectedEntryFee.split('-').map(Number);
          filteredTeams = filteredTeams.filter((team: Team) => {
            const price = team.price ?? 0;
            if (selectedEntryFee.endsWith('-')) {
              return price >= min;
            }
            return price >= min && price <= max;
          });
        }
        return { ...l, teams: filteredTeams };
      })
      .filter((l: League) => {
        const hasMatchingTeam = l.teams && l.teams.length > 0;
        const matchesLeague = leagueSearchLower === '' ||
          (l.name && l.name.toLowerCase().includes(leagueSearchLower)) ||
          (l.league_code && l.league_code.toLowerCase().includes(leagueSearchLower));
        return hasMatchingTeam && matchesLeague;
      });
  }, [leagues, teamSearch, leagueSearch, selectedTier, selectedEntryFee]);

  // Add console.log for debugging
  // React.useEffect(() => {
  //   console.log('Team Search:', teamSearch);
  //   console.log('Filtered Leagues:', filteredLeagues.map((l: League) => ({
  //     leagueName: l.name,
  //     teams: l.teams?.map((t: Team) => t.name)
  //   })));
  // }, [teamSearch, filteredLeagues]);

  // Pass this to SortableTable
  const handleOwnTeam = (league: League, team: Team) => {
    setSelectedTeam({ team, league });
    setPaymentModalOpen(true);
  };

  const handleJoinTeam = (league: League, team: Team) => {
    if (!isAuthenticated) {
      toast.warning('Authentication Required', 'Please sign in to join a team.');
      navigate('/signin');
      return;
    }
    setSelectedTeam({ team, league });
    setIsJoinTeamModalOpen(true);
  };

  return (
    <Box
      w="100%"
      bg="whiteAlpha.50"
      borderRadius="lg"
      px={outerPadding}
      py={{ base: 4, md: 6 }}
      boxShadow="md"
      mb={8}
    >
      <Heading as="h2" size="md" mb={4} color="white">
        {leagueAffiliations.length > 0 ? 'Join Another League' : 'Join a League'}
      </Heading>
      <Box color="white" opacity={0.85} fontSize="md" mb={6} px={0}>
        Get ready to show your stuff! You can <b>acquire a team to own</b> and run or <b>join an existing team</b> to fill a crucial role. If you choose to own a team, payment is due when you join the league. If you want to join a team, you'll arrange a fee, if any, to be paid to the team owner.
        <br /><br />
        If you don't see the team you want, <a href="#" style={{ color: '#63b3ed', textDecoration: 'underline' }}>join the waitlist</a>, and if it becomes available,<br /> we'll let you know! <b>Note:</b> you cannot drop a team, but you can join another league.
      </Box>
      <Tabs variant="unstyled" className="responsive-panel-padding">
        <TabList
          sx={{
            borderBottom: '1px solid #444',
            mb: 2,
          }}
        >
          <Tab
            sx={{
              border: '1px solid',
              borderColor: 'transparent',
              borderBottom: 'none',
              borderRadius: '8px 8px 0 0',
              px: 5,
              py: 1.5,
              fontWeight: 400,
              fontSize: '1rem',
              color: 'whiteAlpha.900',
              bg: 'transparent',
              mr: 1,
              _selected: {
                color: 'blue.400',
                borderColor: '#666 #666 transparent #666',
                bg: 'transparent',
                fontWeight: 500,
                zIndex: 1,
              },
              transition: 'all 0.2s',
            }}
          >
            Own a Team
          </Tab>
          <Tab
            sx={{
              border: '1px solid',
              borderColor: 'transparent',
              borderBottom: 'none',
              borderRadius: '8px 8px 0 0',
              px: 5,
              py: 1.5,
              fontWeight: 400,
              fontSize: '1rem',
              color: 'whiteAlpha.900',
              bg: 'transparent',
              mr: 1,
              _selected: {
                color: 'blue.400',
                borderColor: '#666 #666 transparent #666',
                bg: 'transparent',
                fontWeight: 500,
                zIndex: 1,
              },
              transition: 'all 0.2s',
            }}
          >
            Join a Team
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} py={0}>
            <Box px={{ base: 0, md: 6 }} py={{ base: 0, md: 4 }}>
              <Flex gap={3} mb={4} wrap="wrap">
              <Input 
                placeholder="Team" 
                flexBasis={searchFieldFlexBasis}
                minW={searchFieldMinW}
                flexGrow={1}
                value={teamSearch}
                onChange={e => setTeamSearch(e.target.value)}
              />
              <Input
                placeholder="League"
                flexBasis={searchFieldFlexBasis}
                minW={searchFieldMinW}
                flexGrow={1}
                value={leagueSearch}
                onChange={e => setLeagueSearch(e.target.value)}
              />
              <Select
                placeholder="Tier"
                flexBasis={selectFlexBasis}
                minW={selectMinW}
                value={selectedTier}
                onChange={e => setSelectedTier(e.target.value)}
              >
                <option value="Gold">Gold</option>
                <option value="Silver">Silver</option>
                <option value="Bronze">Bronze</option>
                <option value="Free">Free</option>
              </Select>
              <Select
                placeholder="Fee"
                flexBasis={selectFlexBasis}
                minW={selectMinW}
                value={selectedEntryFee}
                onChange={e => setSelectedEntryFee(e.target.value)}
              >
                <option value="0-100">$0 - $100</option>
                <option value="100-500">$100 - $500</option>
                <option value="500-1000">$500 - $1000</option>
                <option value="1000-2000">$1000 - $2000</option>
                <option value="2000-5000">$2000 - $5000</option>
                <option value="5000-">$5000+</option>
              </Select>
            </Flex>
            </Box>
            {isLoading ? (
              <Box color="white" mt={4}>Loading leagues...</Box>
            ) : error ? (
              <Box color="red.300" mt={4}>Failed to load leagues.</Box>
            ) : (
              <Box width="100%" overflowX="auto" px={tableWrapperPaddingX ?? 0}>
                <SortableTable
                  data={filteredLeagues}
                  onOwnTeam={handleOwnTeam}
                  onJoinTeam={handleJoinTeam}
                />
              </Box>
            )}
          </TabPanel>
          <TabPanel px={0} py={0}>
            <Box px={{ base: 0, md: 6 }} py={{ base: 0, md: 4 }}>
              <Flex gap={3} mb={4} wrap="wrap">
              <Input 
                placeholder="Team" 
                flexBasis={searchFieldFlexBasis}
                minW={searchFieldMinW}
                flexGrow={1}
                value={teamSearch}
                onChange={e => setTeamSearch(e.target.value)}
              />
              <Input
                placeholder="League"
                flexBasis={searchFieldFlexBasis}
                minW={searchFieldMinW}
                flexGrow={1}
                value={leagueSearch}
                onChange={e => setLeagueSearch(e.target.value)}
              />
              <Select placeholder="Jobs" maxW="150px">
                {ROLES.map((role: string) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </Select>
              <Select
                placeholder="Tier"
                flexBasis={selectFlexBasis}
                minW={selectMinW}
              >
                <option value="Gold">Gold</option>
                <option value="Silver">Silver</option>
              </Select>
              <Select
                placeholder="Fee"
                flexBasis={selectFlexBasis}
                minW={selectMinW}
              >
                <option value="$100">$100</option>
                <option value="$50">$50</option>
              </Select>
            </Flex>
            </Box>
            {isLoading ? (
              <Box color="white" mt={4}>Loading leagues...</Box>
            ) : error ? (
              <Box color="red.300" mt={4}>Failed to load leagues.</Box>
            ) : (
              <>
                <Box width="100%" overflowX="auto" px={tableWrapperPaddingX ?? 0}>
                  <SortableTable
                    data={filteredLeagues}
                    showJobsColumn
                    onOwnTeam={handleOwnTeam}
                    onJoinTeam={handleJoinTeam}
                  />
                </Box>
                <Box color="white" fontSize="sm" mt={2} opacity={0.8}>
                  * Ownership fee already paid by owner. When you request to join, the owner may request you pay for portion of the entry fee.
                </Box>
              </>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
      <ProcessPayment
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        team_name={selectedTeam?.team.name || ''}
        league_name={selectedTeam?.league.name || ''}
        tier={selectedTeam?.league.tier || ''}
        price={selectedTeam?.team.price}
        team_id={selectedTeam?.team.id}
        league_id={selectedTeam?.league.id}
        buyer_id={user?.id}
        onSuccess={() => {
          // Refresh the leagues data after successful acquisition
          window.location.reload();
        }}
      />
      <JoinTeamModal
        isOpen={isJoinTeamModalOpen}
        onClose={() => setIsJoinTeamModalOpen(false)}
        team={selectedTeam?.team}
        league={selectedTeam?.league}
      />
    </Box>
  );
};

export default JoinLeagues; 